// Widget-side global error capture.
//
// Per #34: ship NO Sentry SDK in the widget bundle (would consume ~30 KB
// of the 80 KB gzipped budget). Instead a tiny global handler installs
// `error` and `unhandledrejection` listeners and POSTs error JSON to
// `/api/v1/widget-error`, where the admin app captures via the
// server-side Sentry SDK.
//
// Key design properties:
// - Fire-and-forget. Failures NEVER throw back into the widget — that
//   would create a feedback loop where the error reporter itself errors.
// - Source-frame filter. We only forward errors whose stack/filename
//   indicates the widget bundle (or recording-flow chunk). Customer-page
//   errors are NOT our problem and would burn through Sentry quota.
// - Burst throttle. Max 5 errors per 60s window. A runaway `setInterval`
//   that throws on every tick can't flood the relay.
// - Session dedup. Identical errors in the same widget mount fire once.

import type { ApiClient } from './api.ts';

const WINDOW_MS = 60_000;
const MAX_PER_WINDOW = 5;

// Match the widget bundle filename (`w-<version>.js` and chunked deps in
// `chunks/<name>-<version>.js`) AND the dev source path served by Vite
// (`/src/...` from packages/widget/src). Anything else — host page bugs,
// extension code, ad scripts — is filtered out before we burn relay
// quota or Sentry events.
const WIDGET_FRAME_PATTERN = /(?:\/w-\d|\/chunks\/[^/]+-\d|\/packages\/widget\/src\/|\/src\/)/;

interface InstallOpts {
  api: ApiClient;
  workspaceId: string;
  widgetVersion: string;
}

interface Throttle {
  windowStart: number;
  count: number;
  seen: Set<string>;
}

function newThrottle(): Throttle {
  return { windowStart: Date.now(), count: 0, seen: new Set() };
}

function shouldReport(throttle: Throttle, dedupKey: string): boolean {
  const now = Date.now();
  if (now - throttle.windowStart >= WINDOW_MS) {
    throttle.windowStart = now;
    throttle.count = 0;
    throttle.seen.clear();
  }
  if (throttle.count >= MAX_PER_WINDOW) return false;
  if (throttle.seen.has(dedupKey)) return false;
  throttle.seen.add(dedupKey);
  throttle.count += 1;
  return true;
}

function isWidgetFrame(stack: string | undefined, filename: string | undefined): boolean {
  if (filename && WIDGET_FRAME_PATTERN.test(filename)) return true;
  if (stack && WIDGET_FRAME_PATTERN.test(stack)) return true;
  return false;
}

function dedupKeyFor(message: string, stack: string | undefined): string {
  // First two stack lines are usually enough to distinguish unique bugs
  // without exploding the dedup set on transient differences.
  const head = stack ? stack.split('\n').slice(0, 2).join('|') : '';
  return `${message}::${head}`;
}

/**
 * Wires window error + unhandledrejection listeners. Returns an
 * uninstaller in case the host wants to tear down (tests, hot reload).
 */
export function installErrorHandlers(opts: InstallOpts): () => void {
  const throttle = newThrottle();

  const onError = (event: ErrorEvent): void => {
    try {
      const stack = event.error instanceof Error ? event.error.stack : undefined;
      const message = String(event.message ?? event.error?.message ?? 'unknown error');
      if (!isWidgetFrame(stack, event.filename)) return;
      if (!shouldReport(throttle, dedupKeyFor(message, stack))) return;
      void opts.api.submitWidgetError({
        workspaceId: opts.workspaceId,
        errorKind: 'error',
        message,
        stack,
        url: event.filename || undefined,
        lineno: typeof event.lineno === 'number' ? event.lineno : undefined,
        colno: typeof event.colno === 'number' ? event.colno : undefined,
        widgetVersion: opts.widgetVersion,
      });
    } catch {
      // Never throw from the error handler — that's an infinite-loop trap.
    }
  };

  const onRejection = (event: PromiseRejectionEvent): void => {
    try {
      const reason = event.reason as unknown;
      const stack = reason instanceof Error ? reason.stack : undefined;
      const message =
        reason instanceof Error
          ? reason.message
          : typeof reason === 'string'
            ? reason
            : 'unhandled promise rejection';
      if (!isWidgetFrame(stack, undefined)) return;
      if (!shouldReport(throttle, dedupKeyFor(message, stack))) return;
      void opts.api.submitWidgetError({
        workspaceId: opts.workspaceId,
        errorKind: 'unhandledrejection',
        message,
        stack,
        widgetVersion: opts.widgetVersion,
      });
    } catch {
      // Same fail-silent contract as onError.
    }
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
